import { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import api, { checkHealth } from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function GatewaySign() {
  const [company, setCompany] = useState(null);
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [healthOk, setHealthOk] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  
  const [terms, setTerms] = useState({
    terms_1200: false,
    terms_350: false,
    terms_6pct: false,
    privacy: false,
    truth: false,
  });

  useEffect(() => { checkServerHealth(); }, []);
  useEffect(() => { loadCompany(); }, []);

  async function checkServerHealth() {
    const result = await checkHealth();
    setHealthOk(result.ok);
    return result.ok;
  }

  async function loadCompany() {
    try {
      const res = await api.get('/me/company');
      const companyData = res.data.company || res.data;
      if (!companyData || !companyData.id) {
        setError(t('gateway.no_company'));
        return;
      }
      setCompany(companyData);
      const gwRes = await api.get(`/gateway/check/${companyData.id}`);
      if (gwRes.data.has_gateway) {
        setSuccess({ alreadySigned: true, pdf_url: gwRes.data.pdf_url });
      }
    } catch (e) {
      if (e.errorType === 'ENDPOINT_NOT_FOUND' || e.response?.status === 404) {
        setError(t('gateway.no_company'));
      } else {
        setError(e.userMessage || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  }

  const allChecked = Object.values(terms).every(v => v);

  const handleSign = async () => {
    if (!allChecked || !company) return;
    
    const isHealthy = await checkServerHealth();
    if (!isHealthy) {
      setError(t('gateway.error_server_unavailable'));
      return;
    }
    
    setSigning(true);
    setError(null);
    
    try {
      const res = await api.post('/gateway/sign', {
        company_id: company.id,
        accepted_terms_1200: terms.terms_1200,
        accepted_terms_350: terms.terms_350,
        accepted_terms_6pct: terms.terms_6pct,
        accepted_privacy: terms.privacy,
        accepted_truth: terms.truth,
      });
      setSuccess(res.data);
    } catch (e) {
      switch (e.errorType) {
        case 'AUTH_TELEGRAM': setError(t('gateway.error_auth_telegram')); break;
        case 'AUTH_EXPIRED': setError(t('gateway.error_auth_expired')); break;
        case 'PROFILE_NOT_VERIFIED': setError(t('gateway.error_profile_not_verified')); break;
        case 'GATEWAY_EXISTS': setError(t('gateway.error_gateway_exists')); loadCompany(); break;
        case 'TERMS_NOT_ACCEPTED': setError(t('gateway.error_terms_not_accepted')); break;
        case 'NO_COMPANY': setError(t('gateway.error_no_company')); break;
        case 'TIMEOUT': setError(t('gateway.error_timeout')); setRetryCount(c => c + 1); break;
        case 'SERVER_ERROR': setError(t('gateway.error_server', { requestId: e.requestId || '?' })); break;
        default: setError(e.userMessage || t('gateway.error_generic'));
      }
    } finally {
      setSigning(false);
    }
  };

  const handleRetry = async () => {
    setError(null);
    await checkServerHealth();
    if (healthOk) handleSign();
  };

  const navigateTo = (page) => {
    const tg = window.Telegram?.WebApp;
    if (tg) window.location.href = `?screen=${page}`;
  };

  if (loading) {
    return (
      <div className="page">
        <BackButton />
        <div className="loader">{t('common.loading')}</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page">
        <BackButton />
        <div className="success-card">
          <div className="success-icon">✅</div>
          <h2>{success.alreadySigned ? t('gateway.already_signed') : t('gateway.signed_success')}</h2>
          <p>{success.alreadySigned ? 'Переходи к оплате, если активация ещё не подтверждена.' : 'Договор подписан. Следующий шаг — оплата активации 1200 PLN.'}</p>
          {success.pdf_url && (
            <button 
              className="btn btn-primary"
              onClick={async () => {
                try {
                  const response = await api.get(success.pdf_url, { responseType: 'blob' });
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `gateway_contract.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  alert(t('common.error') + ': ' + (e.userMessage || e.message));
                }
              }}
            >
              📄 {t('gateway.download_pdf')}
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => navigateTo('payment_wall')}>
            → Перейти к оплате
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton />
      <h1>🚀 {t('gateway.title')}</h1>
      <p className="subtitle">{t('gateway.subtitle', { company: company?.name })}</p>

      {!healthOk && (
        <div className="warning-box" style={{
          background: 'rgba(255,193,7,0.2)',
          border: '1px solid rgba(255,193,7,0.5)',
          padding: 12, borderRadius: 8, marginBottom: 16,
        }}>
          ⚠️ {t('gateway.server_temp_unavailable')}
          <button className="btn btn-small" onClick={checkServerHealth} style={{ marginLeft: 8 }}>
            {t('gateway.check')}
          </button>
        </div>
      )}

      {error && (
        <div className="error-box" style={{
          background: 'rgba(255,107,107,0.2)',
          border: '1px solid rgba(255,107,107,0.5)',
          padding: 12, borderRadius: 8, marginBottom: 16,
        }}>
          ❌ {error}
          {retryCount > 0 && (
            <button className="btn btn-small" onClick={handleRetry} style={{ marginLeft: 8 }}>
              {t('common.retry')}
            </button>
          )}
        </div>
      )}

      <div className="contract-terms">
        <h3>{t('gateway.terms_title')}</h3>
        
        <label className="checkbox-item">
          <input type="checkbox" checked={terms.terms_1200} onChange={e => setTerms({...terms, terms_1200: e.target.checked})} />
          <span>{t('gateway.term_1200')}</span>
        </label>

        <label className="checkbox-item">
          <input type="checkbox" checked={terms.terms_350} onChange={e => setTerms({...terms, terms_350: e.target.checked})} />
          <span>{t('gateway.term_350')}</span>
        </label>

        <label className="checkbox-item">
          <input type="checkbox" checked={terms.terms_6pct} onChange={e => setTerms({...terms, terms_6pct: e.target.checked})} />
          <span>{t('gateway.term_6pct')}</span>
        </label>

        <label className="checkbox-item">
          <input type="checkbox" checked={terms.privacy} onChange={e => setTerms({...terms, privacy: e.target.checked})} />
          <span>{t('gateway.privacy')}</span>
        </label>

        <label className="checkbox-item">
          <input type="checkbox" checked={terms.truth} onChange={e => setTerms({...terms, truth: e.target.checked})} />
          <span>{t('gateway.truth')}</span>
        </label>
      </div>

      <div className="services-info">
        <h3>{t('gateway.services_title')}</h3>
        <ul>
          <li>✓ {t('gateway.service_1')}</li>
          <li>✓ {t('gateway.service_2')}</li>
          <li>✓ {t('gateway.service_3')}</li>
          <li>✓ {t('gateway.service_4')}</li>
          <li>✓ {t('gateway.service_5')}</li>
        </ul>
      </div>

      <button
        className={`btn btn-sign ${allChecked && healthOk ? 'active' : 'disabled'}`}
        onClick={handleSign}
        disabled={!allChecked || signing || !healthOk}
      >
        {signing ? '⏳ ' + t('gateway.signing') : !healthOk ? '⚠️ ' + t('gateway.server_unavailable') : '✍️ ' + t('gateway.sign_btn')}
      </button>

      <p className="kup-note" style={{ fontSize: 12, opacity: 0.7, marginTop: 16, textAlign: 'center' }}>
        {t('gateway.kup_note')}
      </p>
    </div>
  );
}
